import React from 'react';
import { useSelector } from 'react-redux';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { SuppressionList } from '@mm/state/model/SuppressionListModel';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import css from './ContactSuppression.module.css';
import { WebhookIcon } from '@mm/icons';
import { selectWebhookSuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { Status, Tooltip } from '@mm/components';
import { colors } from '../../../../../styles/colors';
import { CaptureActionEnum } from '@mm/state/model/SuppressionSettingsModel';

const SuppressionWebhookLists = ({
  captureAction,
}: {
  captureAction: CaptureActionEnum;
}) => {
  const connectedPageId = useSelector(activeFacebookPageIdSelector);
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal } = useModalActions();

  const isSuppression = captureAction === CaptureActionEnum.do_not_send;
  const webhookSuppressions = useSelector(selectWebhookSuppressionList).filter(
    w =>
      isSuppression
        ? w.captureAction === captureAction || !w.captureAction
        : w.captureAction === captureAction,
  );

  const suppressionListsCRUDActions = useSuppressionListsCRUDActions();

  const onDeleteSuppressionList = (suppression: SuppressionList) => {
    const deleteCallback = async () => {
      suppressionListsCRUDActions.deleteList(connectedPageId, suppression);
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${suppression.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };

  const onEditSuppressionList = (suppressionList: SuppressionList) => {
    showSlideoutMenu('editWebhoookSuppressionListEditor', {
      suppressionListId: suppressionList.id,
    });
  };

  return (
    <div className={css.list}>
      {webhookSuppressions.map(item => (
        <div className={css.listItem} key={item.id}>
          <div className={css.webhook}>
            <WebhookIcon />
          </div>

          <span
            className={css.listTitle}
            onClick={() => onEditSuppressionList(item)}
          >
            {item.name}
          </span>
          {item.suppressionWebhook?.errorInfo ? (
            <div className={css.listError}>
              <Tooltip
                title={
                  typeof item.suppressionWebhook?.errorInfo === 'string'
                    ? item.suppressionWebhook?.errorInfo
                    : `Failed to call the webhook provided. 
                    Please make sure that the URL is correct and the server is reachable.`
                }
              >
                <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
              </Tooltip>
            </div>
          ) : (
            <span className={css.listCount}>{item.profilesCount}</span>
          )}

          <span className={css.listActions}>
            <Status
              status={item.enabled ? 'Active' : 'Disabled'}
              color={!item.enabled ? colors.grayLight : undefined}
            />

            <div className={css.listAction}>
              <div
                className={css.listActionIcon}
                onClick={() => onEditSuppressionList(item)}
              >
                <EditOutlined />
              </div>
            </div>
            <div className={css.listAction}>
              <div
                className={css.listActionIcon}
                onClick={() => onDeleteSuppressionList(item)}
              >
                <DeleteOutlined />
              </div>
            </div>
          </span>
        </div>
      ))}
    </div>
  );
};

export default SuppressionWebhookLists;
